import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Copy, ChevronDown, ChevronUp } from 'lucide-react'
import { useWalletStore } from '@/stores/useWalletStore'
import { getSwapLog } from '@/services/api/swap'
import { getCoinIcon } from '@/lib/common'
import { toFixed } from '@/lib/decimal'
import { useClipboard } from '@/hooks/useClipboard'
import { NoData } from '@/components/NoData'
import type { SwapLog } from '@/types'

export function SwapRecord() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLogin, session } = useWalletStore()
  const { copy } = useClipboard()

  const [list, setList] = useState<SwapLog[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const pageSize = 20

  const fetchLogs = useCallback(async (pageNum: number, append = false) => {
    if (!isLogin || !session) return
    setLoading(true)
    try {
      const result = await getSwapLog({
        account: session.account.name,
        page: pageNum,
        size: pageSize,
      })
      if (result.status && result.data) {
        const data = result.data as SwapLog[]
        if (append) {
          setList((prev) => [...prev, ...data])
        } else {
          setList(data)
        }
        setHasMore(data.length >= pageSize)
      } else {
        if (!append) setList([])
        setHasMore(false)
      }
    } catch {
      if (!append) setList([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [isLogin, session])

  useEffect(() => {
    fetchLogs(1)
  }, [fetchLogs])

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchLogs(nextPage, true)
  }

  const toggleExpand = (txid: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(txid)) next.delete(txid)
      else next.add(txid)
      return next
    })
  }

  const formatTime = (ts: string) => {
    const date = new Date(ts)
    return date.toLocaleString()
  }

  return (
    <div className="w-full max-w-[600px] mx-auto px-4 py-6 md:py-10 animate-trade-enter">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate({ to: '/swap' })} className="p-1.5 rounded-full hover:bg-[#edf5ed] transition-colors">
          <ArrowLeft className="h-5 w-5 text-foreground" />
        </button>
        <h2 className="text-[20px] font-bold text-foreground">{t('swap.swapRecord')}</h2>
      </div>

      {!isLogin ? (
        <div className="text-center py-10 text-nav">{t('public.noauth')}</div>
      ) : list.length === 0 && !loading ? (
        <NoData />
      ) : (
        <div className="space-y-3">
          {list.map((item, i) => {
            const isExpanded = expandedIds.has(item.txid || String(i))
            return (
              <div key={item.txid || i} className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
                {/* Summary row */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#f9f9f9] transition-colors"
                  onClick={() => toggleExpand(item.txid || String(i))}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <img
                        src={getCoinIcon('', item.symbolIn || '')}
                        className="w-[24px] h-[24px] rounded-full bg-gray-100"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <span className="text-[14px] font-semibold text-foreground">{item.symbolIn}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-nav" />
                    <div className="flex items-center gap-1">
                      <img
                        src={getCoinIcon('', item.symbolOut || '')}
                        className="w-[24px] h-[24px] rounded-full bg-gray-100"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <span className="text-[14px] font-semibold text-foreground">{item.symbolOut}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-nav">{formatTime(item.timestamp)}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-nav" /> : <ChevronDown className="h-4 w-4 text-nav" />}
                  </div>
                </div>

                {/* Amount summary */}
                <div className="px-4 pb-3 flex items-center justify-between text-[13px]">
                  <span className="text-red-500">-{item.amountIn} {item.symbolIn}</span>
                  <span className="text-primary">+{item.amountOut} {item.symbolOut}</span>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-[#f3f3f3] pt-3 space-y-2 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-nav">{t('public.fee')}</span>
                      <span className="text-foreground">{item.fee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-nav">{t('public.price')}</span>
                      <span className="text-foreground">
                        {item.price ? toFixed(parseFloat(item.price), 8) : '-'}
                      </span>
                    </div>
                    {item.txid && (
                      <div className="flex justify-between items-center">
                        <span className="text-nav">TxID</span>
                        <div className="flex items-center gap-1">
                          <a
                            href={`https://bloks.io/transaction/${item.txid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate max-w-[200px]"
                          >
                            {item.txid.slice(0, 16)}...
                          </a>
                          <button onClick={(e) => { e.stopPropagation(); copy(item.txid) }} className="p-0.5 hover:text-primary transition-colors">
                            <Copy className="h-3.5 w-3.5 text-nav" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Load more */}
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="w-full py-3 text-[14px] text-nav hover:text-primary transition-colors"
            >
              {loading ? t('public.loading') : t('swap.loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
